import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthService } from '@dav-core/services/auth.service';
import { ConfigService } from '@dav-core/services/config.service';
import * as fromRoot from '@dav-store/reducers';
import * as UserActions from '@dav-store/user/user.actions';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private previousUrl = '';
  private apiCallsToIgnoreLoading = [
    '/refreshtoken',
    '/getclientinfo',
    '/getcaptcha',
    '/verifyusername',
    '/contractactivity',
  ];
  private apiCallsToIgnoreHidingError = ['/refreshtoken', '/manualdecode', '/getcaptcha'];
  private apiErrorsToIgnore = [{ url: '/manualdecode', status: 330 }];
  private apiCallsToIgnoreToken = ['/token'];

  constructor(
    private configService: ConfigService,
    private authService: AuthService,
    private store$: Store<fromRoot.State>
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let byPassLoading = false;

    this.apiCallsToIgnoreLoading.forEach((url: string) => {
      byPassLoading ||= request.url.includes(url);
    });

    // Bypass loading bar when API calls are made for refresh token
    if (!byPassLoading) {
      this.configService.requestInterceptor();
    }

    let bypassToken = false;
    this.apiCallsToIgnoreToken.forEach((url: string) => (bypassToken = bypassToken || request.url.endsWith(url)));

    let token = '';

    // SSO flow
    if (request.url.endsWith('/ssologin')) {
      const ssoToken = this.authService.getCookie();
      token = `Bearer ${ssoToken}`;
    } else {
      if (sessionStorage.getItem('token')) {
        token = `Bearer ${sessionStorage.getItem('token')}`;
      }
    }

    let langCode = '';
    if (this.configService.getLanguage()) {
      langCode = this.configService.getLanguage();
    }

    let callToken = token;
    if (request.headers.get('TokenType') === 'looker') {
      callToken = request.headers.get('Authorization');
    }

    // Token to bypass in UMU (OAuth)
    const authReq = bypassToken
      ? request
      : request.clone({ headers: new HttpHeaders({ Authorization: callToken, 'Accept-Language': langCode }) });

    return next.handle(authReq).pipe(
      tap((response: HttpResponse<any>) => {
        if (response.status === 200) {
          let noByPass = false;

          this.apiCallsToIgnoreHidingError.forEach((url) => {
            if (request.url.endsWith(url)) {
              noByPass = true;
            }
          });
          if (!noByPass) {
            this.configService.hideErrorMessage();
          }
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // Network Error - When service is down
        this.previousUrl = request.url;

        if (err.status === 403) {
          this.store$.dispatch(new UserActions.LogOutAction());
        }

        if (err.status === 0) {
          err = new HttpErrorResponse({
            status: 100,
            statusText: 'Network Error - service may be down, please try again later.',
          });
        }

        if (this.shouldDisplayError(request.url, err.status)) {
          this.configService.displayErrorMessage(err.status, err.statusText);
        }

        return throwError(err);
      }),
      finalize(() => this.configService.responseInterceptor())
    );
  }

  private shouldDisplayError(url: string, status: number) {
    return !this.apiErrorsToIgnore.some((call) => url.includes(call.url) && status === call.status);
  }
}

// use request interceptor to intercept and add the token to the header of each request
export const requestInterceptor = {
  provide: HTTP_INTERCEPTORS,
  useClass: RequestInterceptor,
  multi: true,
};
